import React, { useRef, useCallback, useMemo, useEffect } from 'react';
import useForkRef from '../../../hooks/use-fork-ref.esm.js';
import ownerDocument from '../../../utils/owner-document.esm.js';
import Box from '../../_primitives/box/box.esm.js';

/* eslint-disable @typescript-eslint/ban-ts-comment */
var TrapFocus = function TrapFocus(props) {
  var children = props.children,
    _props$disableAutoFoc = props.disableAutoFocus,
    disableAutoFocus = _props$disableAutoFoc === void 0 ? false : _props$disableAutoFoc,
    _props$disableEnforce = props.disableEnforceFocus,
    disableEnforceFocus = _props$disableEnforce === void 0 ? false : _props$disableEnforce,
    _props$disableRestore = props.disableRestoreFocus,
    disableRestoreFocus = _props$disableRestore === void 0 ? false : _props$disableRestore,
    getDoc = props.getDoc,
    isEnabled = props.isEnabled,
    open = props.open;
  var ignoreNextEnforceFocus = useRef();
  var sentinelStart = useRef(null);
  var sentinelEnd = useRef(null);
  var nodeToRestore = useRef();
  var rootRef = useRef();
  var handleOwnRef = useCallback(function (instance) {
    rootRef.current = instance;
  }, []);
  // @ts-ignore
  var handleRef = useForkRef(children.ref, handleOwnRef);

  // ⚠️ You may rely on useMemo as a performance optimization, not as a semantic guarantee.
  // https://reactjs.org/docs/../../hooks-reference.html#usememo
  useMemo(function () {
    if (!open || typeof window === 'undefined') {
      return;
    }
    nodeToRestore.current = getDoc().activeElement;
  }, [getDoc, open]);
  useEffect(function () {
    if (!open) {
      return;
    }
    var doc = ownerDocument(rootRef.current);

    // We might render an empty child.
    if (!disableAutoFocus && rootRef.current && !rootRef.current.contains(doc.activeElement)) {
      if (!rootRef.current.hasAttribute('tabIndex')) {
        if (process.env.NODE_ENV !== 'production') {
          console.error(['Tui: the modal content node does not accept focus.', 'For the benefit of assistive technologies, ' + 'the tabIndex of the node is being set to "-1".'].join('\n'));
        }
        rootRef.current.setAttribute('tabIndex', '-1');
      }
      rootRef.current.focus();
    }
    var contain = function contain() {
      if (!doc.hasFocus() || disableEnforceFocus || !isEnabled() || ignoreNextEnforceFocus.current) {
        ignoreNextEnforceFocus.current = false;
        return;
      }
      if (rootRef.current && !rootRef.current.contains(doc.activeElement)) {
        rootRef.current.focus();
      }
    };
    var loopFocus = function loopFocus(event) {
      // 9 = Tab
      if (disableEnforceFocus || !isEnabled() || event.keyCode !== 9) {
        return;
      }

      // Make sure the next tab starts from the right place.
      if (doc.activeElement === rootRef.current) {
        // We need to ignore the next contain as
        // it will try to move the focus back to the rootRef element.
        ignoreNextEnforceFocus.current = true;
        if (event.shiftKey) {
          sentinelEnd.current && sentinelEnd.current.focus();
        } else {
          sentinelStart.current && sentinelStart.current.focus();
        }
      }
    };
    doc.addEventListener('focus', contain, true);
    doc.addEventListener('keydown', loopFocus, true);

    // With Edge, Safari and Firefox, no focus related events are fired when the focused area stops being a focused area
    // e.g. https://bugzilla.mozilla.org/show_bug.cgi?id=559561.
    //
    // The whatwg spec defines how the browser should behave but does not explicitly mention any events:
    // https://html.spec.whatwg.org/multipage/interaction.html#focus-fixup-rule.
    var interval = setInterval(function () {
      contain();
    }, 50);
    return function () {
      clearInterval(interval);
      doc.removeEventListener('focus', contain, true);
      doc.removeEventListener('keydown', loopFocus, true);

      // restoreLastFocus()
      if (!disableRestoreFocus) {
        // In IE 11 it is possible for document.activeElement to be null resulting
        // in nodeToRestore.current being null.
        // Not all elements in IE 11 have a focus method.
        // Once IE 11 support is dropped the focus() call can be unconditional.
        if (nodeToRestore.current && nodeToRestore.current.focus) {
          nodeToRestore.current.focus();
        }
        nodeToRestore.current = null;
      }
    };
  }, [disableAutoFocus, disableEnforceFocus, disableRestoreFocus, isEnabled, open]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    tabIndex: 0,
    ref: sentinelStart,
    "data-test": "sentinelStart"
  }), /*#__PURE__*/React.cloneElement(children, {
    ref: handleRef
  }), /*#__PURE__*/React.createElement(Box, {
    tabIndex: 0,
    ref: sentinelEnd,
    "data-test": "sentinelEnd"
  }));
};

export default TrapFocus;
//# sourceMappingURL=trap-focus.esm.js.map
